<template>
    <Head title="Private Profile" />
    <BaseLayout>
        <div data-testid="user-profile-content" class="flex flex-col gap-10">
            <BasePageHeader
                title="Private Profile"
                subtitle="Nadia's living record of what you've told her, what she's inferred, and where she's still guessing. Only you can see this." />
            <template v-if="profile_completion_summary">
                <!-- The accordion variant replaces ProfileHeader, which is otherwise the only place a
                     user can edit their name and role. Keep lightweight editors so those capabilities
                     aren't lost for users in the COACHING_JOURNEY_ACCORDION rollout. -->
                <div class="bg-white border-0 md:border border-border rounded-lgr px-4 md:px-6 py-4 fs-mask flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div class="flex min-w-0 items-center gap-2">
                        <p class="body-small text-ink-muted shrink-0">Your name</p>
                        <ProfileEditableField field="full_name" :value="user_name ?? ''" :endpoints="endpoints" />
                    </div>
                    <div class="flex min-w-0 items-center gap-2">
                        <p class="body-small text-ink-muted shrink-0">Your role</p>
                        <ProfileEditableField field="job_title" :value="jobTitle" :endpoints="endpoints" placeholder="Add your role" />
                    </div>
                </div>
                <CoachingJourneyAccordion :profile-completion="profile_completion_summary" @select-tab="handleSelectTab" @show-carousel="isInfoCarouselOpen = true" />
            </template>
            <ProfileHeader
                v-else
                :user-name="user_name ?? ''"
                :job-title="jobTitle"
                :years-of-experience="yearsOfExperience"
                :completion-percent="completionPercent"
                :endpoints="endpoints" />
            <BaseTabs ref="profileTabs" v-model="profileTabModel" :tab-ids="profileTabIds" :labels="profileTabTitles" hide-single-tab-list>
                <template #[`content-${TabIds.ARCHETYPE}`]>
                    <div class="pt-10">
                        <ArchetypeTab :perspective-report="user_perspective_report" :perspective-report-enabled="user_perspective_report_enabled" />
                    </div>
                </template>
                <template #[`content-${TabIds.LEARN}`]>
                    <div class="pt-10 flex flex-col gap-10">
                        <ProfileQuestions ref="profileQuestions" :title="profileTabTitles[TabIds.LEARN]" :user-profile-questions="incompleteUserProfileEntries">
                            <template #subtitle>
                                <p class="body-bold text-ink-muted">Answer a few questions for coaching that fits your world.</p>
                            </template>
                        </ProfileQuestions>
                        <ProfileFeedbackSection v-if="user_feedback_enabled" :minimum-response-count="min_user_feedback_count" />
                    </div>
                </template>
                <template #[`content-${TabIds.STYLE}`]>
                    <div class="pt-10 flex flex-col gap-10">
                        <ProfileQuestions :title="profileTabTitles[TabIds.STYLE]" :user-profile-questions="completeUserProfileEntries" :is-profile-questions="false">
                            <template #subtitle>
                                <p class="body-bold text-ink-muted">What Nadia remembers about you — no need to repeat yourself.</p>
                            </template>
                        </ProfileQuestions>
                        <ProfileFeedbackSection v-if="!profileTabIds.includes(TabIds.LEARN) && user_feedback_enabled" :minimum-response-count="min_user_feedback_count" />
                    </div>
                </template>
                <template #[`content-${TabIds.INSIGHTS}`]>
                    <div v-if="!insights || insights.length === 0" class="text-center py-10">
                        <p class="title-body mb-2 text-ink">Let's create your first insight together.</p>
                        <p class="body text-ink-muted">
                            <Link class="underline" href="/coach/start">Start a new chat</Link> so Nadia can build insights from your reflections and conversations.
                        </p>
                    </div>
                    <template v-else>
                        <HomeSectionContainer :title="profileTabTitles[TabIds.INSIGHTS]" subtitle="Discover patterns in your work style and approach." class="pt-10">
                            <div class="grid md:grid-cols-2 gap-4">
                                <HomeInsightsCard
                                    v-for="(insight, index) in insights"
                                    :key="insight.content"
                                    :card-type="CARD_TYPE.INSIGHTS"
                                    :content="insight.content"
                                    cta-button-text="Dive Deeper"
                                    @cta-click="handleInsightCardClick(insight, index)" />
                            </div>
                        </HomeSectionContainer>
                    </template>
                </template>
            </BaseTabs>
        </div>
    </BaseLayout>

    <!-- Info carousel dialog (triggered from Complete Profile info icon) -->
    <!-- Reuses the full-bleed coaching-context carousel, including its slide navigation. -->
    <BaseDialog
        v-if="isInfoCarouselOpen"
        v-model:open="isInfoCarouselOpen"
        layout="custom"
        title="Get more from Nadia"
        dialog-content-class="md:max-w-4xl max-h-[85dvh] md:max-h-[90dvh] bg-surface"
        @update:open="handleInfoCarouselOpenChange">
        <CoachingContextCarousel
            :first-name="user_name ?? undefined"
            location="private_profile"
            :connections-enabled="profile_completion_summary?.connections_enabled ?? true"
            :collab-profile-enabled="profile_completion_summary?.collab_profile_enabled ?? true"
            :calendar-enabled="profile_completion_summary?.calendar_enabled ?? true"
            @dismiss="handleInfoCarouselOpenChange(false)"
            @cta-click="isInfoCarouselOpen = false" />
    </BaseDialog>
</template>

<script lang="ts">
import LeftSidebarLayout from "~vue/layouts/LeftSidebarLayout.vue";
import type { CalendarIntegration, HomepageInsight } from "~vue/types/chat_home";
export default { layout: LeftSidebarLayout };
</script>

<script setup lang="ts">
import { Head, Link, router } from "@inertiajs/vue3";
import { useUrlSearchParams } from "@vueuse/core";
import type { GlobalProperties, PerspectiveReport } from "~vue/appTypes";
import CoachingContextCarousel from "~vue/ChatHome/CoachingContextCarousel.vue";
import HomeInsightsCard from "~vue/ChatHome/HomeInsightsCard.vue";
import HomeSectionContainer from "~vue/ChatHome/HomeSectionContainer.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseLayout from "~vue/components/BaseLayout.vue";
import BasePageHeader from "~vue/components/BasePageHeader.vue";
import BaseTabs from "~vue/components/BaseTabs.vue";
import { usePerspectiveReportImport } from "~vue/composables/usePerspectiveReportImport";
import ArchetypeTab from "~vue/UserProfile/ArchetypeTab.vue";
import CoachingJourneyAccordion from "~vue/UserProfile/CoachingJourneyAccordion.vue";
import ProfileEditableField from "~vue/UserProfile/ProfileEditableField.vue";
import ProfileFeedbackSection from "~vue/UserProfile/ProfileFeedbackSection.vue";
import ProfileHeader from "~vue/UserProfile/ProfileHeader.vue";
import ProfileQuestions from "~vue/UserProfile/ProfileQuestions.vue";
import type { ProfileCompletionSummary, UserProfileData, UserProfileEndpoints, UserProfileEntries } from "~vue/UserProfile/types";
import { openUrl } from "~vue/utils";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { CHATSOURCE } from "~vue/utils/constants";
import { CARD_TYPE } from "~vue/utils/homeCards";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, inject, nextTick, onMounted, onUnmounted, provide, ref, useTemplateRef, watch } from "vue";
enum TabIds {
    ARCHETYPE = "archetype",
    LEARN = "learn",
    STYLE = "style",
    INSIGHTS = "insights",
}

const isTabId = (value: string): value is TabIds => {
    return Object.values(TabIds).includes(value as TabIds);
};

const profileTabTitles = {
    [TabIds.ARCHETYPE]: "Perspective",
    [TabIds.LEARN]: "My Questions",
    [TabIds.STYLE]: "My Notes",
    [TabIds.INSIGHTS]: "My Insights",
};
const { $setEventContext } = inject<GlobalProperties>("globalProperties")!;

type UserProfileProps = {
    user_profile: UserProfileData;
    user_profile_entries: UserProfileEntries;
    user_name: string | null;
    event_context: Record<string, string>;
    endpoints: UserProfileEndpoints;
    user_perspective_report_enabled: boolean;
    user_perspective_report: PerspectiveReport | null;
    perspective_assessment_enabled: boolean;
    insights: HomepageInsight[];
    min_user_feedback_count: number;
    user_feedback_enabled: boolean;
    profile_completion_summary: ProfileCompletionSummary | null;
    calendar_integration: CalendarIntegration | null;
};

const {
    user_profile,
    user_profile_entries,
    user_name = "",
    event_context,
    user_perspective_report_enabled = false,
    user_perspective_report = null,
    perspective_assessment_enabled = false,
    insights = [],
    min_user_feedback_count = 3,
    user_feedback_enabled = false,
    profile_completion_summary = null,
    calendar_integration = null,
} = defineProps<UserProfileProps>();

provide("calendarIntegration", calendar_integration);

// Poll for a Team Tools perspective report in the background. When one is found,
// reload the server-computed accordion and report prop so the Perspective step updates.
// Also re-check once on load when a report already exists, so a retake in Team Tools
// (which produces a newer OpenSearch document but no new "found" event) is picked up.
usePerspectiveReportImport({
    reportEnabled: () => user_perspective_report_enabled,
    assessmentEnabled: () => perspective_assessment_enabled,
    initialReport: user_perspective_report,
    onReportFound: () => router.reload({ only: ["profile_completion_summary", "user_perspective_report"] }),
    refreshExistingReport: true,
});

const jobTitle = computed(() => user_profile.components.job_title);
const yearsOfExperience = computed(() => {
    const yoeAnswer = user_profile_entries.find(({ key }) => key === "years_of_experience")?.answer;
    return yoeAnswer && parseInt(yoeAnswer) ? `${parseInt(yoeAnswer)} Years of Experience` : null;
});

const completionPercent = computed(() => Math.ceil((user_profile_entries.filter(({ answer }) => !!answer).length / user_profile_entries.length) * 100));

const incompleteUserProfileEntries = computed(() => user_profile_entries.filter(({ answer }) => !answer));
const completeUserProfileEntries = computed(() => user_profile_entries.filter(({ answer }) => !!answer));

const profileTabIds = computed(() => {
    const ids = [];
    if (user_perspective_report_enabled) {
        ids.push(TabIds.ARCHETYPE);
    }
    if (incompleteUserProfileEntries.value.length > 0) {
        ids.push(TabIds.LEARN);
    }
    if (completeUserProfileEntries.value.length > 0) {
        ids.push(TabIds.STYLE);
    }
    ids.push(TabIds.INSIGHTS);
    return ids;
});

watch(profileTabIds, (newTabIds) => {
    // If the user deletes their only profile answer, make sure they get back to the first tab
    if (newTabIds.length > 0 && !newTabIds.includes(profileTabModel.value)) {
        profileTabModel.value = newTabIds[0];
    }
});

const profileTabModel = ref(profileTabIds.value[0]);
const hasPerspective = computed(() => !!user_perspective_report);

const profileTabs = useTemplateRef("profileTabs");

const handleSelectTab = async (tabId: string) => {
    if (!isTabId(tabId)) {
        return;
    }
    profileTabModel.value = tabId;
    await nextTick();
    profileTabs.value?.focusTab(tabId);
};

// Guards against firing private_profile_tab_clicked for the initial / programmatic
// tab set during mount (the initial tab is reported via private_profile_viewed instead).
const trackTabChanges = ref(false);

const hashParams = useUrlSearchParams("hash-params");
watch(profileTabModel, (newModel) => {
    if (newModel) {
        hashParams.tab = newModel;
    }
    if (trackTabChanges.value && newModel) {
        void logUserInteraction(AmplitudeEventNames.PRIVATE_PROFILE_TAB_CLICKED, { tab_type: "section", tab: newModel });
    }
});

onMounted(async () => {
    $setEventContext(event_context);
    const hashTab = typeof hashParams.tab === "string" && isTabId(hashParams.tab) ? hashParams.tab : null;
    if (hashTab) {
        profileTabModel.value = hashTab;
    }
    void logUserInteraction(AmplitudeEventNames.PRIVATE_PROFILE_VIEWED, {
        completion_percent: completionPercent.value,
        has_perspective: hasPerspective.value,
        initial_tab: profileTabModel.value,
    });
    // Enable tab-change tracking only after the programmatic mount-time tab set has flushed.
    await nextTick();
    trackTabChanges.value = true;
    if (hashTab) {
        profileTabs.value?.focusTab(hashTab, { behavior: "instant", block: "start" });
    }
});
onUnmounted(() => {
    $setEventContext(event_context);
});

const getInsightSource = (url: string): "v1" | "v2" | "hris" | "unknown" => {
    if (url.includes("values_insight_id=")) {
        return "v1";
    }
    if (url.includes("homepage_insight_v2=")) {
        return "v2";
    }
    if (url.includes("hris_insight=")) {
        return "hris";
    }
    return "unknown";
};

// Prefer the granular source the backend now plumbs through (e.g. the specific HRIS change_type
// like "title_change"); fall back to the coarse URL-derived bucket for v1/v2 insights.
const resolveInsightSource = (insight: HomepageInsight): string => insight.insight_source ?? getInsightSource(insight.url);

const handleInsightCardClick = (insight: HomepageInsight, cardPosition: number) => {
    void logUserInteraction(AmplitudeEventNames.PRIVATE_PROFILE_INSIGHT_CLICKED, {
        insight_source: resolveInsightSource(insight),
        card_position: cardPosition,
    });
    openUrl(insight.url, { queryParams: { chat_source: CHATSOURCE.HOME_INSIGHTS } });
};

const isInfoCarouselOpen = ref(false);

const handleInfoCarouselOpenChange = (isOpen: boolean) => {
    isInfoCarouselOpen.value = isOpen;
    if (!isOpen) {
        void logUserInteraction(AmplitudeEventNames.COACHING_CONTEXT_CAROUSEL_DISMISSED, { location: "private_profile" });
    }
};

// Fire one impression event per rendered insight card. Called when the Insights tab becomes
// active (BaseTabs lazily mounts the panel), so this corresponds to the cards actually rendering.
const trackInsightImpressions = () => {
    insights.forEach((insight, cardPosition) => {
        void logUserInteraction(AmplitudeEventNames.PRIVATE_PROFILE_INSIGHT_VIEWED, {
            insight_source: resolveInsightSource(insight),
            priority_for_engagement: insight.priority_for_engagement ?? null,
            card_position: cardPosition,
        });
    });
};

// Count impressions whenever the Insights tab is active: `immediate` covers the initial load /
// deep-link (#tab=insights, which onMounted applies), and later runs cover tab switches. Declared
// after trackInsightImpressions so the immediate (setup-time) invocation can reference it. Unlike
// private_profile_tab_clicked there's no trackTabChanges guard — impressions should count on first render.
watch(
    profileTabModel,
    (newModel) => {
        if (newModel === TabIds.INSIGHTS) {
            trackInsightImpressions();
        }
    },
    { immediate: true },
);
</script>
